import { useEffect, useMemo, useState } from "react";
import { Check, Plus, Search, UserPlus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Inline customer picker: search existing or create new. onChange(customer|null). */
export function CustomerPicker({ value, onChange }) {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api("/customers").then(setCustomers).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers.slice(0, 8);
    return customers.filter((c) => [c.name, c.email, c.phone].filter(Boolean).some((f) => f.toLowerCase().includes(q))).slice(0, 8);
  }, [customers, query]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Customer name is required"); return; }
    setSaving(true);
    try {
      const c = await api("/customers", { method: "POST", body: JSON.stringify({ name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null }) });
      setCustomers((p) => [c, ...p]);
      onChange(c);
      setCreating(false); setOpen(false); setForm({ name: "", email: "", phone: "" });
      toast.success("Customer created");
    } catch (e) { toast.error(e.message || "Could not create customer"); }
    finally { setSaving(false); }
  };

  return (
    <div className="relative">
      {value ? (
        <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3.5 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-tertiary text-sm font-extrabold text-brand">{value.name.charAt(0).toUpperCase()}</div>
            <div>
              <p className="text-sm font-bold text-midnight">{value.name}</p>
              {(value.phone || value.email) && <p className="text-xs text-muted-foreground">{value.phone || value.email}</p>}
            </div>
          </div>
          <button type="button" onClick={() => onChange(null)} data-testid="customer-clear" className="text-xs font-semibold text-brand-2">Change</button>
        </div>
      ) : (
        <>
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid="customer-picker-toggle"
            className="flex w-full items-center gap-2 rounded-lg border border-input bg-card px-3.5 py-3 text-left text-sm text-muted-foreground">
            <Search className="h-4 w-4" /> Select or create a customer…
          </button>
          {open && (
            <div className="absolute z-20 mt-2 w-full rounded-lg border border-border bg-card p-2 shadow-lg">
              {!creating ? (
                <>
                  <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search customers…" data-testid="customer-search-input"
                    className="mb-2 w-full rounded-md border border-input px-3 py-2 text-sm outline-none focus:border-brand" />
                  <div className="max-h-56 overflow-y-auto">
                    {filtered.map((c) => (
                      <button key={c.id} type="button" onClick={() => { onChange(c); setOpen(false); }} data-testid={`customer-option-${c.id}`}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-secondary">
                        <Check className="h-4 w-4 text-transparent" />{c.name}<span className="ml-auto text-xs text-muted-foreground">{c.phone || c.email || ""}</span>
                      </button>
                    ))}
                    {filtered.length === 0 && <p className="px-2 py-3 text-center text-xs text-muted-foreground">No matches</p>}
                  </div>
                  <button type="button" onClick={() => setCreating(true)} data-testid="customer-create-toggle"
                    className="mt-1 flex w-full items-center gap-2 rounded-md bg-brand-tertiary px-2 py-2 text-sm font-bold text-brand-2"><UserPlus className="h-4 w-4" />Create new customer</button>
                </>
              ) : (
                <div className="space-y-2 p-1">
                  <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name *" data-testid="new-customer-name" className="w-full rounded-md border border-input px-3 py-2 text-sm outline-none focus:border-brand" />
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid="new-customer-phone" className="w-full rounded-md border border-input px-3 py-2 text-sm outline-none focus:border-brand" />
                  <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="new-customer-email" className="w-full rounded-md border border-input px-3 py-2 text-sm outline-none focus:border-brand" />
                  <div className="flex gap-2">
                    <button type="button" onClick={create} disabled={saving} data-testid="new-customer-save" className="flex flex-1 items-center justify-center gap-1 rounded-md bg-brand py-2 text-sm font-bold text-white disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Save</button>
                    <button type="button" onClick={() => setCreating(false)} className="rounded-md border border-border px-3 py-2 text-sm font-semibold">Back</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
